import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const fmtCOP = v => new Intl.NumberFormat('es-CO',{style:'currency',currency:'COP',minimumFractionDigits:0}).format(v||0);
const fmtDate = d => d ? new Date(d).toLocaleDateString('es-CO') : '-';

export default function Facturacion() {
  const [pedidos, setPedidos]   = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [filtro, setFiltro]     = useState('todos');

  const loadPedidos = () => api.get('/pedidos?limit=200').then(r => setPedidos(r.data)).catch(()=>{});
  const loadFacturas = () => api.get('/facturas').then(r => setFacturas(r.data)).catch(()=>{});

  useEffect(() => { loadPedidos(); loadFacturas(); }, []);

  // Pedidos que no tienen factura aún
  const facturadosIds = new Set(facturas.map(f => f.pedido_id));
  const pedidosFiltrados = pedidos.filter(p => {
    const tieneFactura = facturadosIds.has(p.id);
    if (filtro === 'sin_factura') return !tieneFactura && ['entregado','enviado'].includes(p.estado);
    if (filtro === 'facturados') return tieneFactura;
    return true;
  });

  const generarFactura = async (pedido) => {
    setLoading(true);
    try {
      // Obtener items del pedido
      const r = await api.get(`/pedidos/${pedido.id}`);
      const p = r.data;
      await api.post('/facturas', {
        pedido_id:        p.id,
        nombre_cliente:   p.nombre_cliente,
        documento_cliente:p.documento_cliente,
        email_cliente:    p.email_cliente,
        subtotal:         p.subtotal,
        descuento:        p.descuento || 0,
        iva:              0,
        notas:            `Pedido ${p.numero} — ${p.ciudad_entrega}`
      });
      toast.success('Factura generada ✅');
      loadFacturas();
    } catch(e) {
      toast.error(e.response?.data?.error || 'Error al generar factura');
    } finally { setLoading(false); }
  };

  const estadoColor = e => ({
    nuevo:'badge-blue', preparando:'badge-yellow', en_proceso:'badge-yellow',
    enviado:'badge-blue', entregado:'badge-green', cancelado:'badge-red'
  })[e] || 'badge-gray';

  return (
    <div>
      <div style={{ background:'#fff', borderBottom:'1px solid var(--border)', padding:'14px 28px', position:'sticky', top:0, zIndex:10, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <h1 style={{ fontSize:18, fontWeight:900, color:'var(--forest)' }}>Facturación</h1>
        <div style={{ display:'flex', gap:8 }}>
          {[
            { v:'todos',       l:'Todos los pedidos' },
            { v:'sin_factura', l:'Sin factura' },
            { v:'facturados',  l:'Facturados' },
          ].map(f => (
            <button key={f.v} onClick={() => setFiltro(f.v)} style={{
              padding:'6px 14px', borderRadius:'var(--r-full)', border:'none', cursor:'pointer',
              fontFamily:'var(--font-body)', fontWeight:600, fontSize:12,
              background: filtro===f.v ? '#9FBE6A' : 'rgba(28,58,30,0.06)',
              color: filtro===f.v ? '#1C3A1E' : 'var(--forest)',
            }}>{f.l}</button>
          ))}
        </div>
      </div>

      <div style={{ padding:'24px 28px' }}>
        {/* Resumen */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16, marginBottom:24 }}>
          {[
            { label:'Total pedidos',    val: pedidos.length,                                          color:'var(--forest)' },
            { label:'Sin factura',      val: pedidos.filter(p => !facturadosIds.has(p.id) && ['entregado','enviado'].includes(p.estado)).length, color:'var(--terra)' },
            { label:'Facturados',       val: facturas.length,                                         color:'var(--sage)' },
          ].map(m => (
            <div key={m.label} className="metric-card">
              <div className="metric-label">{m.label}</div>
              <div className="metric-value" style={{ color:m.color }}>{m.val}</div>
            </div>
          ))}
        </div>

        {/* Tabla */}
        <div className="card">
          <div className="card-header"><span className="card-title">Pedidos ({pedidosFiltrados.length})</span></div>
          <div style={{ overflowX:'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Pedido</th>
                  <th>Cliente</th>
                  <th>Fecha</th>
                  <th>Total</th>
                  <th>Estado</th>
                  <th>Factura</th>
                  <th>Acción</th>
                </tr>
              </thead>
              <tbody>
                {pedidosFiltrados.map(p => {
                  const factura = facturas.find(f => f.pedido_id === p.id);
                  return (
                    <tr key={p.id}>
                      <td><strong style={{ color:'var(--forest)', fontFamily:'monospace' }}>{p.numero}</strong></td>
                      <td>
                        <div style={{ fontWeight:600, fontSize:13 }}>{p.nombre_cliente}</div>
                        <div style={{ fontSize:11, color:'var(--ink-faint)' }}>{p.email_cliente}</div>
                      </td>
                      <td style={{ fontSize:12 }}>{fmtDate(p.created_at)}</td>
                      <td><strong>{fmtCOP(p.total)}</strong></td>
                      <td><span className={`badge ${estadoColor(p.estado)}`}>{p.estado}</span></td>
                      <td>
                        {factura
                          ? <span className="badge badge-green">{factura.numero}</span>
                          : <span className="badge badge-gray">Sin factura</span>
                        }
                      </td>
                      <td>
                        {!factura && ['entregado','enviado','nuevo','preparando','en_proceso'].includes(p.estado) && (
                          <button
                            onClick={() => generarFactura(p)}
                            disabled={loading}
                            style={{ padding:'5px 12px', borderRadius:'var(--r-sm)', border:'none', background:'#9FBE6A', color:'#1C3A1E', fontWeight:700, fontSize:12, cursor:'pointer' }}
                          >
                            🧾 Generar
                          </button>
                        )}
                        {factura && (
                          <span style={{ fontSize:12, color:'var(--ink-faint)' }}>{fmtDate(factura.created_at)}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {pedidosFiltrados.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign:'center', padding:40, color:'var(--ink-faint)' }}>No hay pedidos</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
